import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';

/**
 * Same protocol allowlist as react-markdown's `defaultUrlTransform`, so clipboard HTML
 * does not keep `javascript:` / `data:` / `vbscript:` hrefs the on-screen renderer strips.
 */
const SAFE_URL_PROTOCOL = /^(https?|ircs?|mailto|xmpp)$/i;

type HastNode = {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

const isSafeMarkdownUrl = (value: string): boolean => {
  const colon = value.indexOf(':');
  if (colon === -1) {
    return true;
  }

  const slash = value.indexOf('/');
  const questionMark = value.indexOf('?');
  const numberSign = value.indexOf('#');
  if (
    (slash !== -1 && colon > slash) ||
    (questionMark !== -1 && colon > questionMark) ||
    (numberSign !== -1 && colon > numberSign)
  ) {
    return true;
  }

  return SAFE_URL_PROTOCOL.test(value.slice(0, colon));
};

const sanitizeUrlProperty = (node: HastNode, key: string) => {
  const properties = node.properties;
  const url = properties?.[key];
  if (properties && typeof url === 'string' && !isSafeMarkdownUrl(url)) {
    properties[key] = '';
  }
};

const rehypeSafeUrls = () => (tree: HastNode) => {
  const walk = (node: HastNode) => {
    if (node.tagName === 'a') {
      sanitizeUrlProperty(node, 'href');
    }
    if (node.tagName === 'img') {
      sanitizeUrlProperty(node, 'src');
    }
    node.children?.forEach(walk);
  };

  walk(tree);
};

const markdownProcessor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkRehype)
  .use(rehypeSafeUrls)
  .use(rehypeStringify);

/**
 * Converts GFM markdown to an HTML fragment for rich clipboard paste (Docs/Word).
 * Uses the same remark-gfm plugin as chat rendering — not a full HTML document.
 */
export const markdownToHtml = (markdown: string): string => {
  const trimmed = markdown.trim();
  if (!trimmed) {
    return '';
  }

  return String(markdownProcessor.processSync(trimmed));
};
